/**
 * Editable table for reviewing and editing portfolio company investments.
 *
 * Features:
 * - Inline editing (click to edit, blur/Enter to save)
 * - Add/delete rows
 * - Totals comparison (extracted vs PDF-stated)
 * - Visual indicators for edited/new/deleted rows
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Plus,
  Trash2,
  AlertTriangle,
  Check,
  Building2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import type { PortfolioInvestment, FieldProvenance } from "@/lib/extraction-api";

interface EditablePortfolioTableProps {
  /** Initial investments from API */
  investments: PortfolioInvestment[];
  /** PDF-stated totals for comparison */
  pdfStatedTotals?: {
    totalCost?: number;
    totalFairValue?: number;
  };
  /** Provenance data from document metadata keyed by investment name */
  portfolioProvenance?: Record<string, FieldProvenance> | null;
  /** Callback when data changes */
  onChange: (investments: PortfolioInvestment[], hasChanges: boolean) => void;
  /** Whether the table is read-only */
  readOnly?: boolean;
  /** Callback when provenance button is clicked (to navigate PDF) */
  onProvenanceClick?: (page: number, text: string) => void;
}

type EditingCell = {
  rowIndex: number;
  field: keyof PortfolioInvestment;
} | null;

// Generate a temporary ID for new rows
function generateTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Format currency value for display
function formatCurrency(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

// Parse currency input to number
function parseCurrency(input: string): number | null {
  const cleaned = input.replace(/[^0-9.-]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

export function EditablePortfolioTable({
  investments: initialInvestments,
  pdfStatedTotals,
  portfolioProvenance,
  onChange,
  readOnly = false,
  onProvenanceClick,
}: EditablePortfolioTableProps) {
  const [expanded, setExpanded] = useState(true);
  const [investments, setInvestments] = useState<PortfolioInvestment[]>(initialInvestments);
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [editValue, setEditValue] = useState("");
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [editedIds, setEditedIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  // Track original IDs to detect changes
  const originalIds = useMemo(
    () => new Set(initialInvestments.map((inv) => inv.id).filter(Boolean)),
    [initialInvestments]
  );

  // Update parent when investments change
  useEffect(() => {
    const activeInvestments = investments.filter(
      (inv) => !deletedIds.has(inv.id || "")
    );
    const hasChanges =
      deletedIds.size > 0 ||
      newIds.size > 0 ||
      editedIds.size > 0;
    onChange(activeInvestments, hasChanges);
  }, [investments, deletedIds, newIds, editedIds, onChange]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Calculate totals (excluding deleted rows)
  const totals = useMemo(() => {
    const active = investments.filter((inv) => !deletedIds.has(inv.id || ""));
    return {
      count: active.length,
      totalCost: active.reduce((sum, inv) => sum + (inv.cost_basis || 0), 0),
      totalFairValue: active.reduce((sum, inv) => sum + (inv.fair_value || 0), 0),
      totalGL: active.reduce((sum, inv) => sum + (inv.unrealized_gain_loss || 0), 0),
    };
  }, [investments, deletedIds]);

  // Calculate differences from PDF stated totals
  const differences = useMemo(() => {
    if (!pdfStatedTotals) return null;
    return {
      costDiff:
        pdfStatedTotals.totalCost != null
          ? totals.totalCost - pdfStatedTotals.totalCost
          : null,
      fvDiff:
        pdfStatedTotals.totalFairValue != null
          ? totals.totalFairValue - pdfStatedTotals.totalFairValue
          : null,
    };
  }, [totals, pdfStatedTotals]);

  // Start editing a cell
  const startEditing = useCallback(
    (rowIndex: number, field: keyof PortfolioInvestment) => {
      if (readOnly) return;
      const inv = investments[rowIndex];
      if (deletedIds.has(inv.id || "")) return;

      const value = inv[field];
      let displayValue = "";
      if (field === "cost_basis" || field === "fair_value") {
        displayValue = value != null ? String(value) : "";
      } else {
        displayValue = value != null ? String(value) : "";
      }

      setEditingCell({ rowIndex, field });
      setEditValue(displayValue);
    },
    [investments, deletedIds, readOnly]
  );

  // Commit edit
  const commitEdit = useCallback(() => {
    if (!editingCell) return;

    const { rowIndex, field } = editingCell;
    const inv = investments[rowIndex];
    let newValue: string | number | null = editValue.trim() || null;

    // Parse currency fields
    if (field === "cost_basis" || field === "fair_value") {
      newValue = parseCurrency(editValue);
    }

    // Update investment
    const updated = { ...inv, [field]: newValue };

    // Recalculate unrealized G/L if cost or FV changed
    if (field === "cost_basis" || field === "fair_value") {
      const cost = field === "cost_basis" ? newValue : inv.cost_basis;
      const fv = field === "fair_value" ? newValue : inv.fair_value;
      updated.unrealized_gain_loss =
        cost != null && fv != null ? (fv as number) - (cost as number) : null;
    }

    setInvestments((prev) => {
      const newInvestments = [...prev];
      newInvestments[rowIndex] = updated;
      return newInvestments;
    });

    // Track as edited (if not a new row)
    if (inv.id && !newIds.has(inv.id)) {
      setEditedIds((prev) => new Set(prev).add(inv.id!));
    }

    setEditingCell(null);
    setEditValue("");
  }, [editingCell, editValue, investments, newIds]);

  // Cancel edit
  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue("");
  }, []);

  // Handle key press in input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitEdit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelEdit();
      } else if (e.key === "Tab") {
        e.preventDefault();
        commitEdit();
        // Move to next editable cell
        if (editingCell) {
          const fields: (keyof PortfolioInvestment)[] = [
            "investment_name",
            "industry_sector",
            "cost_basis",
            "fair_value",
          ];
          const currentFieldIndex = fields.indexOf(editingCell.field);
          const nextFieldIndex = e.shiftKey
            ? currentFieldIndex - 1
            : currentFieldIndex + 1;

          if (nextFieldIndex >= 0 && nextFieldIndex < fields.length) {
            startEditing(editingCell.rowIndex, fields[nextFieldIndex]);
          } else if (!e.shiftKey && editingCell.rowIndex < investments.length - 1) {
            startEditing(editingCell.rowIndex + 1, fields[0]);
          }
        }
      }
    },
    [commitEdit, cancelEdit, editingCell, startEditing, investments.length]
  );

  // Add new row
  const addRow = useCallback(() => {
    const tempId = generateTempId();
    const newInvestment: PortfolioInvestment = {
      id: tempId,
      fund_name: "",
      statement_date: "",
      investment_name: "",
      investment_type: null,
      industry_sector: null,
      cost_basis: null,
      fair_value: null,
      unrealized_gain_loss: null,
      ownership_percentage: null,
      shares_units: null,
      investment_stage: null,
      geographic_region: null,
      initial_investment_date: null,
      notes: null,
    };
    setInvestments((prev) => [...prev, newInvestment]);
    setNewIds((prev) => new Set(prev).add(tempId));
    // Start editing the name field of the new row
    setTimeout(() => {
      startEditing(investments.length, "investment_name");
    }, 0);
  }, [investments.length, startEditing]);

  // Delete row (soft delete)
  const deleteRow = useCallback((rowIndex: number) => {
    const inv = investments[rowIndex];
    if (!inv.id) return;

    if (newIds.has(inv.id)) {
      // Remove completely if it's a new row
      setInvestments((prev) => prev.filter((_, i) => i !== rowIndex));
      setNewIds((prev) => {
        const next = new Set(prev);
        next.delete(inv.id!);
        return next;
      });
    } else {
      // Soft delete for existing rows
      setDeletedIds((prev) => new Set(prev).add(inv.id!));
    }
  }, [investments, newIds]);

  // Render editable cell
  const renderCell = (
    inv: PortfolioInvestment,
    rowIndex: number,
    field: keyof PortfolioInvestment,
    isCurrency: boolean = false
  ) => {
    const isEditing =
      editingCell?.rowIndex === rowIndex && editingCell?.field === field;
    const isDeleted = deletedIds.has(inv.id || "");
    const value = inv[field];

    if (isEditing) {
      return (
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          className="h-7 text-xs w-full"
        />
      );
    }

    const displayValue = isCurrency
      ? formatCurrency(value as number | null)
      : value || "—";

    return (
      <span
        className={`cursor-pointer hover:bg-muted/50 px-1 py-0.5 rounded -mx-1 ${
          isDeleted ? "line-through text-muted-foreground" : ""
        }`}
        onClick={() => !readOnly && startEditing(rowIndex, field)}
      >
        {displayValue}
      </span>
    );
  };

  // Get row background class
  const getRowClass = (inv: PortfolioInvestment) => {
    if (deletedIds.has(inv.id || "")) return "bg-red-50 opacity-50";
    if (newIds.has(inv.id || "")) return "bg-green-50";
    if (editedIds.has(inv.id || "")) return "bg-yellow-50";
    return "";
  };

  if (investments.length === 0 && !readOnly) {
    return (
      <div className="border rounded-md p-6 text-center">
        <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground mb-3">
          No portfolio companies extracted
        </p>
        <Button variant="outline" size="sm" onClick={addRow}>
          <Plus className="h-4 w-4 mr-1" />
          Add Investment
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <button
        type="button"
        className="flex items-center gap-2 mb-3 w-full text-left"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <Building2 className="h-4 w-4" />
        <h4 className="font-semibold text-sm">Schedule of Investments</h4>
        <Badge variant="secondary" className="ml-auto text-xs">
          {totals.count} positions
        </Badge>
      </button>

      {expanded && (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs w-[200px]">Company</TableHead>
                <TableHead className="text-xs w-[120px]">Sector</TableHead>
                <TableHead className="text-xs text-right w-[110px]">Cost</TableHead>
                <TableHead className="text-xs text-right w-[110px]">Fair Value</TableHead>
                <TableHead className="text-xs text-right w-[100px]">G/L</TableHead>
                {!readOnly && <TableHead className="text-xs w-[40px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {investments.map((inv, i) => (
                <TableRow key={inv.id || i} className={getRowClass(inv)}>
                  <TableCell className="text-xs font-medium py-1">
                    <span className="flex items-center gap-1">
                      {renderCell(inv, i, "investment_name")}
                      {(() => {
                        // Prefer provenance from document metadata (has correct page + text)
                        const prov = portfolioProvenance?.[inv.investment_name];
                        const page = prov?.page ?? inv.source_page;
                        const text = prov?.text ?? inv.investment_name;
                        if (!page || !onProvenanceClick) return null;
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 px-1.5 text-xs text-primary hover:bg-primary/10 flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onProvenanceClick(page, text);
                                }}
                              >
                                p.{page}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <p className="text-xs font-medium">Source (Page {page})</p>
                              <p className="text-xs text-muted-foreground mt-1">"{text}"</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground py-1">
                    {renderCell(inv, i, "industry_sector")}
                  </TableCell>
                  <TableCell className="text-xs text-right py-1">
                    {renderCell(inv, i, "cost_basis", true)}
                  </TableCell>
                  <TableCell className="text-xs text-right py-1">
                    {renderCell(inv, i, "fair_value", true)}
                  </TableCell>
                  <TableCell
                    className={`text-xs text-right py-1 ${
                      (inv.unrealized_gain_loss || 0) >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {formatCurrency(inv.unrealized_gain_loss)}
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="py-1">
                      {!deletedIds.has(inv.id || "") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:opacity-100 hover:bg-red-100"
                          onClick={() => deleteRow(i)}
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}

              {/* Extracted Totals Row */}
              <TableRow className="bg-muted/50 font-semibold">
                <TableCell className="text-xs py-2">
                  Extracted Total ({totals.count})
                </TableCell>
                <TableCell />
                <TableCell className="text-xs text-right py-2">
                  {formatCurrency(totals.totalCost)}
                </TableCell>
                <TableCell className="text-xs text-right py-2">
                  {formatCurrency(totals.totalFairValue)}
                </TableCell>
                <TableCell
                  className={`text-xs text-right py-2 ${
                    totals.totalGL >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {formatCurrency(totals.totalGL)}
                </TableCell>
                {!readOnly && <TableCell />}
              </TableRow>

              {/* PDF Stated Totals Row (if available) */}
              {pdfStatedTotals && (pdfStatedTotals.totalCost != null || pdfStatedTotals.totalFairValue != null) && (
                <>
                  <TableRow className="bg-muted/30">
                    <TableCell className="text-xs py-2 text-muted-foreground">
                      PDF Stated Total
                    </TableCell>
                    <TableCell />
                    <TableCell className="text-xs text-right py-2 text-muted-foreground">
                      {pdfStatedTotals.totalCost != null
                        ? formatCurrency(pdfStatedTotals.totalCost)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-right py-2 text-muted-foreground">
                      {pdfStatedTotals.totalFairValue != null
                        ? formatCurrency(pdfStatedTotals.totalFairValue)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-right py-2 text-muted-foreground">
                      —
                    </TableCell>
                    {!readOnly && <TableCell />}
                  </TableRow>

                  {/* Difference Row */}
                  {differences && (
                    <TableRow>
                      <TableCell className="text-xs py-2 text-muted-foreground">
                        Difference
                      </TableCell>
                      <TableCell />
                      <TableCell className="text-xs text-right py-2">
                        {differences.costDiff != null ? (
                          <span
                            className={`flex items-center justify-end gap-1 ${
                              Math.abs(differences.costDiff) < 100
                                ? "text-green-600"
                                : Math.abs(differences.costDiff) < 1000
                                ? "text-yellow-600"
                                : "text-red-600"
                            }`}
                          >
                            {Math.abs(differences.costDiff) < 100 ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Tooltip>
                                <TooltipTrigger>
                                  <AlertTriangle className="h-3 w-3" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  Difference exceeds threshold
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {formatCurrency(differences.costDiff)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-right py-2">
                        {differences.fvDiff != null ? (
                          <span
                            className={`flex items-center justify-end gap-1 ${
                              Math.abs(differences.fvDiff) < 100
                                ? "text-green-600"
                                : Math.abs(differences.fvDiff) < 1000
                                ? "text-yellow-600"
                                : "text-red-600"
                            }`}
                          >
                            {Math.abs(differences.fvDiff) < 100 ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Tooltip>
                                <TooltipTrigger>
                                  <AlertTriangle className="h-3 w-3" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  Difference exceeds threshold
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {formatCurrency(differences.fvDiff)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell />
                      {!readOnly && <TableCell />}
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>

          {/* Add Row Button */}
          {!readOnly && (
            <div className="p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground hover:text-foreground"
                onClick={addRow}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Investment
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EditablePortfolioTable;
